import { Controller } from 'react-hook-form';
import { Account, AccountType } from '../types';
import SidepanelDefaultRow from '../../side-panel/side-panel-default-row';
import SidepanelDateRow from '../sidepanel-date-row';
import SidepanelSelectRow from '../sidepanel-select-row';
import SidepanelTextRow from '../sidepanel-text-row';
import AccountsSelector from '../../accounts-selector/accounts-selector';
import { SelectItemProps } from '../../select/types';
import ButtonPseudo from '../../button/button-pseudo';
import IconRefresh from '../../icons/refresh';
import InputText from '../../inputs/input-text';
import Autocomplete from '../../autocomplete/autocomplete';
import formatBalance from '../../../helpers/format-balance';
import SidepanelAmountRow from '../sidepanel-amount-row';
import SidepanelTextareaRow from '../sidepanel-textarea-row';
import { GeneralLedgerContentFormTypes } from './types';

import cn from './general-ledger.module.css';
import styles from '../transaction.module.css';

export default function GeneralLedgerContentForm({
  children,
  control,
  currencies,
  debitAccounts,
  creditAccounts,
  debitAccountBalance,
  creditAccountBalance,
  debitIsLoading,
  creditIsLoading,
  onUpdateBalance,
  isEditable,
  errors,
  onSearchAutocomplete,
  accountCategories,
  accountsCategoriesLoading,
  setDebitAccounts,
  setCreditAccounts,
  setDebitAccountQuery,
  setCreditAccountQuery,
  debitBalanceIsLoading,
  creditBalanceIsLoading,
  debitBalances,
  creditBalances,
  allDebitAccountIsLoading,
  allCreditAccountIsLoading,
  currenciesList,
  currencyId,
  overdraftEnabled,
  debitAccountNumber,
  creditAccountNumber,
}: GeneralLedgerContentFormTypes): React.JSX.Element {
  const enableEdit = Boolean(control);

  const currentDebitAccount = (debitAccounts as Account[])?.find(
    (item) => item.fullNumber === debitAccountNumber,
  );
  const currentCreditAccount = (creditAccounts as Account[])?.find(
    (item) => item.fullNumber === creditAccountNumber,
  );

  const currentDebitBalance = debitBalances?.find(
    (item) => item.currencyId === currencyId,
  );

  const currentCreditBalance = creditBalances?.find(
    (item) => item.currencyId === currencyId,
  );

  return (
    <>
      {children}
      <Controller
        control={control}
        name="debitAccountId"
        defaultValue=""
        render={({ field: { name, value } }) => (
          <input type="hidden" name={name} value={value} tabIndex={-1} />
        )}
      />
      <Controller
        control={control}
        name="creditAccountId"
        defaultValue=""
        render={({ field: { name, value } }) => (
          <input type="hidden" name={name} value={value} tabIndex={-1} />
        )}
      />
      <div className={cn.transactionRowsWrapper}>
        <SidepanelDateRow
          title="Date"
          value=""
          name="transferDate"
          placeholder="Select Date"
          control={control}
          enableEdit={enableEdit}
          disabled={!isEditable}
          tabIndex={!isEditable ? -1 : 0}
          error={errors?.transferDate?.message}
        />
      </div>
      <div className={cn.transactionRowsWrapper}>
        {setDebitAccountQuery && setDebitAccounts && onSearchAutocomplete ? (
          <SidepanelDefaultRow>
            <Controller
              control={control}
              name="debitAccountNumber"
              render={({ field: { value, name, onChange } }) => (
                <AccountsSelector
                  title="Debit account"
                  placeholder="Enter name or number..."
                  className={cn.selectDebit}
                  accounts={accountCategories}
                  isLoading={accountsCategoriesLoading}
                  selectedAccount={value}
                  accountsIsLoading={debitIsLoading}
                  onSearch={(searchText) =>
                    onSearchAutocomplete(name, searchText)
                  }
                  setAccountQuery={setDebitAccountQuery}
                  accountsList={(debitAccounts as Account[]) || []}
                  setAccounts={setDebitAccounts}
                  setSelectedAccount={(number) => onChange(number)}
                  fullWidth
                  disabled={!isEditable}
                  tabIndex={!isEditable ? -1 : 0}
                  error={errors?.debitAccountNumber?.message}
                />
              )}
            />
          </SidepanelDefaultRow>
        ) : (
          <SidepanelDefaultRow>
            <Controller
              name="debitAccountNumber"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Autocomplete
                  title="Debit account"
                  list={(debitAccounts as SelectItemProps[]) || []}
                  placeholder="Choose Debit account"
                  name="debitAccountNumber"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                  fullWidth
                  error={errors?.debitAccountNumber?.message}
                  tabIndex={!isEditable ? -1 : 0}
                  disabled={!isEditable}
                  withSuffix
                />
              )}
            />
          </SidepanelDefaultRow>
        )}

        {(!overdraftEnabled ||
          (currentDebitAccount &&
            (!currentDebitAccount.overdraftEnabled ||
              !currentDebitBalance?.overdraftEnabled))) && (
          <div className={`${cn.balanceRow} ${cn.withMargin}`}>
            <InputText
              name="debitBalance"
              title="Current balance Debit"
              value={
                debitAccountBalance?.value !== undefined
                  ? `${formatBalance(
                      debitAccountBalance.value,
                      currenciesList?.find((item) => item.id === currencyId),
                    )} ${currenciesList?.find((item) => item.id === currencyId)
                      ?.name}`
                  : '-'
              }
              placeholder=""
              tabIndex={-1}
              disabled
            />
            <ButtonPseudo
              icon={<IconRefresh />}
              onClick={() =>
                debitAccountBalance?.accountId &&
                onUpdateBalance &&
                onUpdateBalance(
                  AccountType.DEBIT,
                  debitAccountBalance.accountId,
                )
              }
              className={`${cn.updateBalanceButton} ${
                debitBalanceIsLoading ? cn.loading : ''
              } `}
              disabled={!debitAccountBalance?.accountId || !isEditable}
              tabIndex={-1}
            />
          </div>
        )}
        {overdraftEnabled &&
          currentDebitAccount?.overdraftEnabled &&
          currentDebitBalance?.overdraftEnabled && (
            <div
              className={`${styles.overdraftBalanceWrapper} ${styles.withMargin}`}
            >
              <div className={styles.overdraftBalanceValue}>
                <InputText
                  disabled
                  tabIndex={-1}
                  name="balance"
                  placeholder=""
                  title="Total available balance"
                  className={styles.overdraftBalance}
                  value={
                    debitAccountBalance?.value !== undefined
                      ? `${formatBalance(
                          debitAccountBalance?.value,
                          currenciesList?.find(
                            (item) => item.id === currencyId,
                          ),
                        )} ${currencies?.find(
                          (item) => item.value === currencyId,
                        )?.name}`
                      : '-'
                  }
                />
                <ButtonPseudo
                  icon={<IconRefresh />}
                  onClick={() =>
                    debitAccountBalance?.accountId &&
                    onUpdateBalance &&
                    onUpdateBalance(
                      AccountType.DEBIT,
                      debitAccountBalance.accountId,
                    )
                  }
                  className={`${cn.updateBalanceButton} ${
                    debitBalanceIsLoading ? cn.loading : ''
                  } `}
                  disabled={!debitAccountBalance?.accountId || !isEditable}
                  tabIndex={-1}
                />
              </div>
              <div className={styles.overdraftBalanceValue}>
                <InputText
                  disabled
                  tabIndex={-1}
                  name="availableOverdraft"
                  placeholder=""
                  title="Remaining overdraft"
                  value={
                    currentDebitBalance && currentDebitBalance?.overdraftEnabled
                      ? `${formatBalance(
                          // eslint-disable-next-line no-nested-ternary
                          currentDebitBalance?.value < 0
                            ? (currentDebitBalance?.overdraftValue as number) -
                                Math.abs(currentDebitBalance?.value) <
                              0
                              ? 0
                              : (currentDebitBalance?.overdraftValue as number) -
                                Math.abs(currentDebitBalance?.value)
                            : (currentDebitBalance?.overdraftValue as number),
                          currenciesList?.find(
                            (item) => item.id === currencyId,
                          ),
                        )} ${currencies?.find(
                          (item) => item.value === currencyId,
                        )?.name}`
                      : '-'
                  }
                />
              </div>
              <div className={styles.overdraftBalanceValue}>
                <InputText
                  disabled
                  tabIndex={-1}
                  name="overdraftLimit"
                  placeholder=""
                  title="Overdraft limit"
                  value={
                    currentDebitBalance && currentDebitBalance?.overdraftEnabled
                      ? `${formatBalance(
                          currentDebitBalance?.overdraftValue as number,
                          currenciesList?.find(
                            (item) => item.id === currencyId,
                          ),
                        )} ${currencies?.find(
                          (item) => item.value === currencyId,
                        )?.name}`
                      : '-'
                  }
                />
              </div>
            </div>
          )}

        {setCreditAccountQuery && setCreditAccounts && onSearchAutocomplete && (
          <SidepanelDefaultRow>
            <Controller
              control={control}
              name="creditAccountNumber"
              render={({ field: { value, name, onChange } }) => (
                <AccountsSelector
                  title="Credit account"
                  placeholder="Enter name or number..."
                  className={cn.selectCredit}
                  accounts={accountCategories}
                  isLoading={accountsCategoriesLoading}
                  selectedAccount={value}
                  accountsIsLoading={creditIsLoading}
                  onSearch={(searchText) =>
                    onSearchAutocomplete(name, searchText)
                  }
                  setAccountQuery={setCreditAccountQuery}
                  accountsList={creditAccounts}
                  setAccounts={setCreditAccounts}
                  setSelectedAccount={(number) => onChange(number)}
                  fullWidth
                  disabled={!isEditable}
                  tabIndex={!isEditable ? -1 : 0}
                  error={errors?.creditAccountNumber?.message}
                />
              )}
            />
          </SidepanelDefaultRow>
        )}
        {(!overdraftEnabled ||
          (currentCreditAccount &&
            (!currentCreditAccount.overdraftEnabled ||
              !currentCreditBalance?.overdraftEnabled))) && (
          <div className={cn.balanceRow}>
            <InputText
              name="creditBalance"
              title="Current balance Credit"
              value={
                creditAccountBalance?.value !== undefined
                  ? `${formatBalance(
                      creditAccountBalance.value,
                      currenciesList?.find((item) => item.id === currencyId),
                    )} ${currenciesList?.find((item) => item.id === currencyId)
                      ?.name}`
                  : '-'
              }
              placeholder=""
              tabIndex={-1}
              disabled
            />

            <ButtonPseudo
              icon={<IconRefresh />}
              onClick={() =>
                creditAccountBalance?.accountId &&
                onUpdateBalance &&
                onUpdateBalance(
                  AccountType.CREDIT,
                  creditAccountBalance.accountId,
                )
              }
              className={`${cn.updateBalanceButton} ${
                creditBalanceIsLoading ? cn.loading : ''
              }`}
              disabled={!creditAccountBalance?.accountId || !isEditable}
              tabIndex={-1}
            />
          </div>
        )}
        {overdraftEnabled &&
          currentCreditAccount?.overdraftEnabled &&
          currentCreditBalance?.overdraftEnabled && (
            <div className={styles.overdraftBalanceWrapper}>
              <div className={styles.overdraftBalanceValue}>
                <InputText
                  disabled
                  tabIndex={-1}
                  name="balance"
                  placeholder=""
                  title="Total available balance"
                  className={styles.overdraftBalance}
                  value={
                    creditAccountBalance?.value !== undefined
                      ? `${formatBalance(
                          creditAccountBalance?.value,
                          currenciesList?.find(
                            (item) => item.id === currencyId,
                          ),
                        )} ${currencies?.find(
                          (item) => item.value === currencyId,
                        )?.name}`
                      : '-'
                  }
                />
                <ButtonPseudo
                  icon={<IconRefresh />}
                  onClick={() =>
                    creditAccountBalance?.accountId &&
                    onUpdateBalance &&
                    onUpdateBalance(
                      AccountType.CREDIT,
                      creditAccountBalance.accountId,
                    )
                  }
                  className={`${cn.updateBalanceButton} ${
                    creditBalanceIsLoading ? cn.loading : ''
                  } `}
                  disabled={!creditAccountBalance?.accountId || !isEditable}
                  tabIndex={-1}
                />
              </div>
              <div className={styles.overdraftBalanceValue}>
                <InputText
                  disabled
                  tabIndex={-1}
                  name="availableOverdraft"
                  placeholder=""
                  title="Remaining overdraft"
                  value={
                    currentCreditBalance &&
                    currentCreditBalance?.overdraftEnabled
                      ? `${formatBalance(
                          // eslint-disable-next-line no-nested-ternary
                          currentCreditBalance?.value < 0
                            ? (currentCreditBalance?.overdraftValue as number) -
                                Math.abs(currentCreditBalance?.value) <
                              0
                              ? 0
                              : (currentCreditBalance?.overdraftValue as number) -
                                Math.abs(currentCreditBalance?.value)
                            : (currentCreditBalance?.overdraftValue as number),
                          currenciesList?.find(
                            (item) => item.id === currencyId,
                          ),
                        )} ${currencies?.find(
                          (item) => item.value === currencyId,
                        )?.name}`
                      : '-'
                  }
                />
              </div>
              <div className={styles.overdraftBalanceValue}>
                <InputText
                  disabled
                  tabIndex={-1}
                  name="overdraftLimit"
                  placeholder=""
                  title="Overdraft limit"
                  value={
                    currentCreditBalance &&
                    currentCreditBalance?.overdraftEnabled
                      ? `${formatBalance(
                          currentCreditBalance?.overdraftValue as number,
                          currenciesList?.find(
                            (item) => item.id === currencyId,
                          ),
                        )} ${currencies?.find(
                          (item) => item.value === currencyId,
                        )?.name}`
                      : '-'
                  }
                />
              </div>
            </div>
          )}
      </div>
      <div className={cn.transactionColumnWrapper}>
        <SidepanelAmountRow
          title="Amount"
          value=""
          name="sum"
          placeholder="Enter amount..."
          control={control}
          enableEdit={enableEdit}
          disabled={
            !isEditable ||
            (!debitBalances && !creditBalances) ||
            !currencies?.length
          }
          tabIndex={
            !isEditable ||
            (!debitBalances && !creditBalances) ||
            !currencies?.length
              ? -1
              : 0
          }
          error={errors?.sum?.message}
          currenciesList={currenciesList}
          currencyId={currencyId}
        />

        <SidepanelSelectRow
          title="Currency"
          value=""
          name="currencyId"
          placeholder="Choose currency"
          control={control}
          selectList={currencies}
          enableEdit={enableEdit}
          disabled={
            !isEditable ||
            (!debitBalances && !creditBalances) ||
            !currencies?.length
          }
          tabIndex={
            !isEditable ||
            (!debitBalances && !creditBalances) ||
            !currencies?.length
              ? -1
              : 0
          }
          error={errors?.currencyId?.message}
        />
      </div>
      {(!allCreditAccountIsLoading || !allDebitAccountIsLoading) &&
        debitBalances &&
        creditBalances &&
        !currencies?.length && (
          <div className={`${styles.fee} ${styles.noCurrencies}`}>
            No available currencies yet
          </div>
        )}
      <SidepanelTextRow
        title="Document number"
        value=""
        name="documentNumber"
        placeholder="Enter document number..."
        control={control}
        enableEdit={enableEdit}
        disabled={!isEditable}
        tabIndex={!isEditable ? -1 : 0}
        error={errors?.documentNumber?.message}
      />
      <SidepanelTextareaRow
        title="Info"
        value=""
        name="info"
        placeholder="Enter a description of the transaction..."
        control={control}
        enableEdit={enableEdit}
        disabled={!isEditable}
        tabIndex={!isEditable ? -1 : 0}
        error={errors?.info?.message}
        maxLength={140}
      />
    </>
  );
}
